import { registry } from './registry';
import { scaleShape } from './shapes';
import type {
  AreaSpec,
  HitSpec,
  ProjectileSpec,
  ResolvedSkill,
  SkillAction,
  SkillDef,
  SkillStage,
  StatusApply,
  TripodDef,
} from './skillTypes';
import { type Element, type SuperArmor, maxSuperArmor, stepStagger } from './types';

export const MAX_SKILL_LEVEL = 12;
/** Skill level at which each tripod (facet) tier unlocks. */
export const TRIPOD_UNLOCK = [4, 7, 10] as const;

/**
 * Damage scaling by skill level. Authored coefficients are the level-10 values;
 * level 1 deals 50% of that and every level beyond 10 adds 5.5%.
 */
export const levelMult = (level: number): number => Math.max(0.5, 1 + (level - 10) * 0.0555);

/** Cumulative skill points needed to reach a level (4 / 20 / 48 at levels 4 / 7 / 10). */
export const SKILL_POINT_COST = [0, 0, 1, 2, 4, 8, 12, 20, 28, 38, 48, 60, 72];
export const pointsForLevel = (level: number): number => SKILL_POINT_COST[Math.min(level, MAX_SKILL_LEVEL)] ?? 0;

export function tripodTiersUnlocked(level: number): number {
  return TRIPOD_UNLOCK.filter((l) => level >= l).length;
}

/** Builds the executable version of a skill for a given level and tripod choice. */
export function resolveSkill(def: SkillDef, level = 1, tripodIds: readonly (string | null)[] = []): ResolvedSkill {
  const s: ResolvedSkill = {
    ...structuredClone(def),
    level,
    dmgMult: 1,
    critRateAdd: 0,
    critDmgAdd: 0,
    atkSpeedMult: 1,
    vsWeakMult: 1,
    vsBossMult: 1,
    defPen: 0,
    damageReduction: 0,
    identityGainMult: 1,
    extraStatus: [],
    tripods: [],
  };
  const available = registry.tripods.get(def.id) ?? [];
  const unlocked = tripodTiersUnlocked(level);
  for (const id of tripodIds) {
    if (!id) continue;
    const t = available.find((x) => x.id === id);
    if (!t || t.tier > unlocked) continue;
    t.apply(s);
    s.tripods.push(t.id);
  }
  return s;
}

// ---------------------------------------------------------------------------
// Traversal helpers used by tripods.

function forEachStage(s: ResolvedSkill, fn: (st: SkillStage) => void): void {
  s.stages.forEach(fn);
  if (s.holding?.finisher) fn(s.holding.finisher);
}

function forEachAction(s: ResolvedSkill, fn: (a: SkillAction) => void): void {
  forEachStage(s, (st) => st.actions.forEach(fn));
  s.holding?.tick.forEach(fn);
}

export function forEachHit(s: ResolvedSkill, fn: (h: HitSpec) => void): void {
  forEachAction(s, (a) => {
    if (a.kind === 'hit') fn(a.hit);
    else if (a.kind === 'area') fn(a.area.hit);
    else if (a.kind === 'projectile') {
      fn(a.proj.hit);
      if (a.proj.explode) fn(a.proj.explode);
    }
  });
}

function forEachArea(s: ResolvedSkill, fn: (a: AreaSpec) => void): void {
  forEachAction(s, (a) => {
    if (a.kind === 'area') fn(a.area);
  });
}

function forEachProjectile(s: ResolvedSkill, fn: (p: ProjectileSpec) => void): void {
  forEachAction(s, (a) => {
    if (a.kind === 'projectile') fn(a.proj);
  });
}

// ---------------------------------------------------------------------------
// Tripod effect DSL. Each helper returns a function that mutates a resolved
// skill; `tripod()` composes several into a TripodDef.

export type Mod = (s: ResolvedSkill) => void;

export const T = {
  cd: (seconds: number): Mod => (s) => {
    s.cooldown = Math.max(1, s.cooldown + seconds);
  },
  cdPct: (pct: number): Mod => (s) => {
    s.cooldown = Math.max(1, s.cooldown * (1 + pct));
  },
  mp: (pct: number): Mod => (s) => {
    s.mana = Math.max(0, Math.round(s.mana * (1 + pct)));
  },
  dmg: (pct: number): Mod => (s) => {
    s.dmgMult *= 1 + pct;
  },
  crit: (add: number): Mod => (s) => {
    s.critRateAdd += add;
  },
  critDmg: (add: number): Mod => (s) => {
    s.critDmgAdd += add;
  },
  atkSpeed: (pct: number): Mod => (s) => {
    s.atkSpeedMult *= 1 + pct;
  },
  aoe: (pct: number): Mod => (s) => {
    const k = 1 + pct;
    forEachHit(s, (h) => {
      if (h.shape.kind === 'rect') h.shape = { ...h.shape, width: h.shape.width * k };
      else h.shape = scaleShape(h.shape, k);
    });
  },
  range: (metres: number): Mod => (s) => {
    if (s.maxRange) s.maxRange += metres;
    forEachAction(s, (a) => {
      if (a.kind === 'move' || a.kind === 'teleport') a.distance += metres;
    });
    forEachProjectile(s, (p) => {
      p.range += metres;
    });
    forEachHit(s, (h) => {
      if (h.shape.kind === 'rect') h.shape = { ...h.shape, length: h.shape.length + metres };
    });
  },
  superArmor: (level: SuperArmor): Mod => (s) => {
    s.superArmor = maxSuperArmor(s.superArmor, level);
  },
  stagger: (steps: number): Mod => (s) => {
    s.stagger = stepStagger(s.stagger, steps);
    forEachHit(s, (h) => {
      if (h.stagger) h.stagger = stepStagger(h.stagger, steps);
    });
  },
  weakPoint: (n: number): Mod => (s) => {
    s.weakPoint = (s.weakPoint ?? 0) + n;
  },
  element: (el: Element, status?: StatusApply): Mod => (s) => {
    s.element = el;
    forEachHit(s, (h) => {
      h.element = el;
    });
    if (status) s.extraStatus.push(status);
  },
  status: (st: StatusApply): Mod => (s) => {
    s.extraStatus.push(st);
  },
  vsWeak: (pct: number): Mod => (s) => {
    s.vsWeakMult *= 1 + pct;
  },
  vsBoss: (pct: number): Mod => (s) => {
    s.vsBossMult *= 1 + pct;
  },
  defPen: (pct: number): Mod => (s) => {
    s.defPen = Math.min(0.9, s.defPen + pct);
  },
  damageReduction: (pct: number): Mod => (s) => {
    s.damageReduction = Math.min(0.9, s.damageReduction + pct);
  },
  identityGain: (pct: number): Mod => (s) => {
    s.identityGainMult *= 1 + pct;
  },
  charges: (n: number): Mod => (s) => {
    s.charges = n;
  },
  counter: (on: boolean): Mod => (s) => {
    s.counter = on;
  },
  synergy: (st: StatusApply): Mod => (s) => {
    s.synergy = st;
  },
  noPositional: (bonus: number): Mod => (s) => {
    s.positional = 'none';
    s.dmgMult *= 1 + bonus;
  },
  reaction: (kind: NonNullable<HitSpec['reaction']>): Mod => (s) => {
    forEachHit(s, (h) => {
      if (!h.friendly) h.reaction = kind;
    });
  },
  /** Adds an action to a stage (defaults to the last stage). */
  addAction: (action: SkillAction, stage = -1): Mod => (s) => {
    const st = stage < 0 ? s.stages[s.stages.length - 1] : s.stages[stage];
    if (!st) return;
    st.actions.push(action);
    st.actions.sort((a, b) => a.at - b.at);
    const end = action.at + 0.15;
    if (st.duration < end) st.duration = end;
  },
  /** Multiplies the duration of persistent areas. */
  areaDuration: (add: number): Mod => (s) => {
    forEachArea(s, (a) => {
      if (a.duration && a.duration > 0) a.duration += add;
    });
  },
  hitCount: (mult: number): Mod => (s) => {
    forEachHit(s, (h) => {
      if (h.count && h.count > 1) h.count = Math.round(h.count * mult);
    });
  },
  holdLonger: (seconds: number): Mod => (s) => {
    if (s.holding) s.holding.maxHold += seconds;
  },
  castFaster: (pct: number): Mod => (s) => {
    if (s.castTime) s.castTime *= 1 - pct;
  },
  custom: (fn: Mod): Mod => fn,
};

export function tripod(id: string, tier: 1 | 2 | 3, name: string, description: string, ...mods: Mod[]): TripodDef {
  return { id, tier, name, description, apply: (s) => mods.forEach((m) => m(s)) };
}

/** Effective cooldown after cooldown reduction. */
export function effectiveCooldown(s: ResolvedSkill, cdr: number): number {
  return s.cooldown * (1 - Math.min(0.6, Math.max(0, cdr)));
}
